import { useCallback, useEffect, useRef, useState } from 'react';

import type { PhotoSize } from './photo-crop';
import { loadPhotoSize, problemWithFile, UNREADABLE_MESSAGE } from './photo-files';
import { shrinkPhoto } from './photo-shrink';
import { needsShrink } from './photo-shrink-plan';

export interface PhotoSelection {
  file: File;
  /** A temporary address the browser can show the file from. */
  url: string;
  size: PhotoSize;
}

/** Thrown when a newer pick has taken over, so this one's work is thrown away quietly. */
class StalePick extends Error {}

export type ReportProblem = (message: string, file: File | null) => void;

/**
 * Reading a chosen photo: checks the file, measures it, and holds it (with its temporary address)
 * until it is cleared. When picks overlap, only the latest one counts, and a pick that lost never
 * frees the address of the one that won.
 */
export function usePhotoSelection(report: ReportProblem) {
  const [selection, setSelection] = useState<PhotoSelection | null>(null);
  const [reading, setReading] = useState(false);
  const latest = useRef(0);
  const held = useRef<string | null>(null);

  const free = useCallback((url: string | null) => {
    if (url) URL.revokeObjectURL(url);
  }, []);

  const clear = useCallback(() => {
    free(held.current);
    held.current = null;
    setSelection(null);
  }, [free]);

  useEffect(
    () => () => {
      latest.current += 1;
      free(held.current);
    },
    [free],
  );

  /** Resolves with the photo to frame, which is a smaller copy when the chosen one was too big. */
  const choose = useCallback(
    async (file: File): Promise<PhotoSelection | null> => {
      const mine = ++latest.current;
      const refused = problemWithFile(file);
      if (refused) {
        setReading(false);
        report(refused, file);
        return null;
      }
      setReading(true);
      const created: string[] = [URL.createObjectURL(file)];
      try {
        let accepted: PhotoSelection = {
          file,
          url: created[0],
          size: await loadPhotoSize(created[0]),
        };
        if (needsShrink(file.size, accepted.size)) {
          const shrunk = await shrinkPhoto(file, accepted.url, accepted.size);
          created.push(URL.createObjectURL(shrunk.file));
          accepted = { file: shrunk.file, url: created[1], size: shrunk.size };
        }
        if (mine !== latest.current) throw new StalePick();
        setReading(false);
        free(held.current);
        held.current = accepted.url;
        setSelection(accepted);
        // Only the address that is being kept stays; the original's, if shrunk, is freed.
        for (const url of created) if (url !== accepted.url) free(url);
        return accepted;
      } catch {
        for (const url of created) free(url);
        if (mine === latest.current) {
          setReading(false);
          report(UNREADABLE_MESSAGE, file);
        }
        return null;
      }
    },
    [free, report],
  );

  return { selection, reading, choose, clear };
}
